/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { defineMessages } from 'react-intl'

import { isCCMFeatureActivated } from '@modules/ccm-admin-lib/feature'
import getCCMServiceSelectionRoutes from '@modules/ccm-admin-lib/routes'
import getProjectRoutes from '@modules/project-admin-pages/routes'
import RoutablePage from '@modules/routing/RoutablePage'
import Feature from '@modules/utils/feature'
import OneConsole from '@modules/one-console'

import Adminconsoles from '@/components/Adminconsoles'
import APIConsole from '@/components/APIConsole'
import ApiKeys from '@/components/ApiKeys'
import ClusterOverview from '@/components/Cluster/ClusterOverview'
import ClusterSnapshots from '@/components/Cluster/Snapshots'
import SnapshotDetails from '@/components/Cluster/Snapshots/SnapshotDetails'
import Container from '@/components/Container'
import ContainerSet from '@/components/ContainerSet'
import ContainerSets from '@/components/ContainerSets'
import Deployment from '@/components/Deployment'
import DeploymentGettingStarted from '@/components/Deployment/DeploymentGettingStarted'
import HealthStatus from '@/components/Deployment/HealthStatus'
import HealthStatusDetails from '@/components/Deployment/HealthStatusDetails'
import DeploymentOverview from '@/components/Deployment/Overview'
import DeploymentOperations from '@/components/DeploymentOperations'
import DeploymentSecurity from '@/components/DeploymentSecurity'
import CreateDeploymentTrustRelationship from '@/components/DeploymentTrustRelationships/CreateDeploymentTrustRelationship'
import ManageAccountTrustRelationship from '@/components/DeploymentTrustRelationships/ManageAccountTrustRelationship'
import ManageDirectTrustRelationship from '@/components/DeploymentTrustRelationships/ManageDirectTrustRelationship'
import ManageExternalTrustRelationship from '@/components/DeploymentTrustRelationships/ManageExternalTrustRelationship'
import NotFound from '@/components/ErrorPages/NotFound'
import SsoErrors from '@/components/ErrorPages/SsoErrors'
import Host from '@/components/Host'
import Hosts from '@/components/Hosts'
import IlmMigration from '@/components/IlmMigration'
import Logout from '@/components/Logout'
import HeapDumps from '@/components/ManageHeapDumps'
import Organization from '@/components/Organization'
import Organizations from '@/components/Organization/Organizations'
import Platform from '@/components/Platform'
import PlatformRegionsRoute from '@/components/PlatformRegionsRoute'
import RedirectToDeploymentResource from '@/components/RedirectToDeploymentResource'
import Region from '@/components/Region'
import RegionWrapper from '@/components/Region/RegionWrapper'
import ElasticStackVersions from '@/components/RegionAdmin/ElasticStackVersions'
import RegionSecurity from '@/components/RegionSecurity'
import RegionSettings from '@/components/RegionSettings'
import Runner from '@/components/Runner'
import SnapshotRepositoryManagement from '@/components/SnapshotRepositoryManagement'
import CreateSnapshotRepository from '@/components/SnapshotRepositoryManagement/CreateSnapshotRepository'
import EditSnapshotRepository from '@/components/SnapshotRepositoryManagement/EditSnapshotRepository'
import StackActivityFeedRoute from '@/components/StackActivityFeedRoute'
import StackDeploymentActivity from '@/components/StackDeploymentActivity'
import CreateStackDeploymentRoute from '@/components/StackDeploymentEditor/CreateStackDeploymentRoute'
import EditStackDeployment from '@/components/StackDeploymentEditor/EditStackDeployment'
import EditStackDeploymentAdvancedRoute from '@/components/StackDeploymentEditor/EditStackDeploymentAdvancedRoute'
import StackDeploymentSlider from '@/components/StackDeploymentSlider'
import StackDeploymentsRoute from '@/components/StackDeploymentsRoute'
import {
  DeploymentTemplateCreate as TopologyDeploymentTemplateCreate,
  DeploymentTemplateEdit as TopologyDeploymentTemplateEdit,
  DeploymentTemplateList as TopologyDeploymentTemplateList,
  DeploymentTemplateView as TopologyDeploymentTemplateView,
} from '@/components/Topology/DeploymentTemplates'
import {
  InstanceConfigurationCreate as TopologyInstanceConfigurationCreate,
  InstanceConfigurationEdit as TopologyInstanceConfigurationEdit,
  InstanceConfigurationList as TopologyInstanceConfigurationList,
  InstanceConfigurationView as TopologyInstanceConfigurationView,
} from '@/components/Topology/InstanceConfigurations'
import Topology from '@/components/Topology/Topology'
import Users from '@/components/Users'
import UserSettings from '@/components/UserSettings'
import { getSliderPrettyName } from '@/lib/sliders/messages'
import {
  getSupportedSliderInstanceTypes,
  getSupportedSliderInstanceTypesWithoutEs,
} from '@/lib/sliders/support'
import { isFeatureActivated } from '@/selectors/config'

import App from './components/App'
import AppRoot from './components/AppRoot'
import LoginIndex from './components/LoginIndex'
import BasicLogin from './components/LoginIndex/BasicLogin'
import SsoBasedLogin from './components/LoginIndex/SsoBasedLogin'
import SsoReceiveToken from './components/LoginIndex/SsoReceiveToken'
import LogsMetrics from './components/LogsMetrics'
import {
  AddLdapProvider,
  AddSamlProvider,
  EditLdapProvider,
  EditSamlProvider,
  ExternalAuthenticationList,
} from './components/Management/ExternalAuthentication'
import AddActiveDirectoryProvider from './components/Management/ExternalAuthentication/activeDirectory/AddActiveDirectoryProvider'
import EditActiveDirectoryProvider from './components/Management/ExternalAuthentication/activeDirectory/EditActiveDirectoryProvider'
import ManageRegions from './components/Management/ManageRegions'
import SecurityClusterWrapper from './components/Management/SecurityClusterWrapper'
import ManageUsers from './components/Management/Users'
import TrustManagement from './components/TrustManagement'
import User from './components/User'

import type { ReduxState } from '@/types/redux'
import type { IntlShape } from 'react-intl'
import type { RouteObject } from 'react-router-dom'

const messages = defineMessages({
  activityFeedSliderRouteTitle: {
    id: `adminconsole-route-titles.activity-feed-slider`,
    defaultMessage: `{name} Activity Feed`,
  },
  sliderActivityPageTitle: {
    id: `adminconsole-route-titles.deployment.activity-page.slider`,
    defaultMessage: `{name} Activity`,
  },
  sliderManagePageTitle: {
    id: `adminconsole-route-titles.deployment.manage-page.slider`,
    defaultMessage: `Manage {name}`,
  },
})

export function getRoutes({ intl, state }: { intl: IntlShape; state: ReduxState }): RouteObject[] {
  const { formatMessage } = intl
  const manageRbac = isFeatureActivated(state, Feature.manageRbac)
  const isOneConsoleEnabled = isFeatureActivated(state, Feature.oneConsole)

  const routes: RouteObject[] = [
    {
      element: <AppRoot />,
      children: [
        ...getAnonymousRoutes(),
        {
          element: <App />,
          children: [
            ...getProjectRoutes(),
            ...getStackDeploymentRoutes(isOneConsoleEnabled),
            ...getCCMServiceSelectionRoutes(),
            ...getOrganizationsRoutes(),
            ...getSaasUsersRoutes(),
            ...getPlatformRoutes(),
            ...getAccountLevelRoutes(),
          ],
        },
        {
          path: '*',
          element: (
            <RoutablePage title='Not found'>
              <NotFound />
            </RoutablePage>
          ),
        },
      ],
    },
  ]

  return routes

  function getAnonymousRoutes(): RouteObject[] {
    return [
      {
        path: '/login',
        element: (
          <RoutablePage title='Login'>
            <LoginIndex />
          </RoutablePage>
        ),
      },
      {
        path: '/login/basic',
        element: (
          <RoutablePage title='Login'>
            <BasicLogin />
          </RoutablePage>
        ),
      },
      {
        path: '/login/sso',
        element: (
          <RoutablePage title='Login'>
            <SsoBasedLogin />
          </RoutablePage>
        ),
      },
      {
        path: '/logout',
        element: (
          <RoutablePage title='Logging out'>
            <Logout />
          </RoutablePage>
        ),
      },
      {
        path: '/sso/token',
        element: (
          <RoutablePage title='Authenticating'>
            <SsoReceiveToken />
          </RoutablePage>
        ),
      },

      {
        path: '/errors/sso',
        element: (
          <RoutablePage title='Error'>
            <SsoErrors />
          </RoutablePage>
        ),
      },
    ]
  }

  function getStackDeploymentRoutes(oneConsoleEnabled: boolean): RouteObject[] {
    return [
      {
        path: 'deployments/resolve/:resourceType/:regionId/:resourceId',
        element: <RedirectToDeploymentResource />,
      },
      {
        path: 'deployments/resolve/:resourceType/:regionId/:resourceId/*',
        element: <RedirectToDeploymentResource />,
      },
      {
        path: 'deployments',
        children: [
          {
            index: true,
            element: (
              <RoutablePage title='Deployments'>
                <StackDeploymentsRoute />
              </RoutablePage>
            ),
          },
          {
            path: 'create',
            element: (
              <RoutablePage title='Create Deployment'>
                <CreateStackDeploymentRoute />
              </RoutablePage>
            ),
          },
          {
            path: ':deploymentId',
            element: <Deployment />,
            children: [
              {
                index: true,
                element: (
                  <RoutablePage title='Overview'>
                    <DeploymentOverview />
                  </RoutablePage>
                ),
              },
              {
                path: 'getting-started',
                element: (
                  <RoutablePage title='Overview'>
                    <DeploymentGettingStarted />
                  </RoutablePage>
                ),
              },
              {
                path: 'edit',
                element: (
                  <RoutablePage title='Edit deployment'>
                    <EditStackDeployment />
                  </RoutablePage>
                ),
              },
              {
                path: 'health',
                element: (
                  <RoutablePage title='Health'>
                    <HealthStatus />
                  </RoutablePage>
                ),
              },
              {
                path: 'health/:issue',
                element: (
                  <RoutablePage title='Health status'>
                    <HealthStatusDetails />
                  </RoutablePage>
                ),
              },
              {
                path: 'edit/advanced',
                element: (
                  <RoutablePage title='Edit deployment'>
                    <EditStackDeploymentAdvancedRoute />
                  </RoutablePage>
                ),
              },
              {
                path: 'elasticsearch',
                element: (
                  <RoutablePage title='Manage Elasticsearch'>
                    <ClusterOverview />
                  </RoutablePage>
                ),
              },
              {
                path: 'elasticsearch/console',
                element: (
                  <RoutablePage title='Deployment console'>
                    <APIConsole />
                  </RoutablePage>
                ),
              },
              ...(oneConsoleEnabled
                ? [
                    {
                      path: 'elasticsearch/one-console',
                      element: (
                        <RoutablePage title='Console'>
                          <OneConsole />
                        </RoutablePage>
                      ),
                    },
                  ]
                : []),
              {
                path: 'elasticsearch/snapshots',
                element: (
                  <RoutablePage title='Deployment snapshots'>
                    <ClusterSnapshots />
                  </RoutablePage>
                ),
              },
              {
                path: 'elasticsearch/snapshots/:snapshotName',
                element: (
                  <RoutablePage title='Snapshot :snapshotName'>
                    <SnapshotDetails />
                  </RoutablePage>
                ),
              },

              ...getSupportedSliderInstanceTypesWithoutEs().map((sliderInstanceType) => ({
                path: sliderInstanceType,
                element: (
                  <RoutablePage
                    title={formatMessage(messages.sliderManagePageTitle, {
                      name: formatMessage(getSliderPrettyName({ sliderInstanceType })),
                    })}
                  >
                    <StackDeploymentSlider sliderInstanceType={sliderInstanceType} />
                  </RoutablePage>
                ),
              })),
              {
                path: 'activity',
                children: [
                  {
                    index: true,
                    element: (
                      <RoutablePage title='Deployment Activity'>
                        <StackDeploymentActivity />
                      </RoutablePage>
                    ),
                  },
                  ...getSupportedSliderInstanceTypes().map((sliderInstanceType) => ({
                    path: sliderInstanceType,
                    element: (
                      <RoutablePage
                        title={formatMessage(messages.sliderActivityPageTitle, {
                          name: formatMessage(getSliderPrettyName({ sliderInstanceType })),
                        })}
                      >
                        <StackDeploymentActivity sliderInstanceType={sliderInstanceType} />
                      </RoutablePage>
                    ),
                  })),
                ],
              },
              {
                path: 'logs-metrics',
                element: (
                  <RoutablePage title='Logs and metrics'>
                    <LogsMetrics />
                  </RoutablePage>
                ),
              },
              {
                path: 'security',
                element: (
                  <RoutablePage title='Security'>
                    <DeploymentSecurity />
                  </RoutablePage>
                ),
              },
              {
                path: 'security/trust/create',
                element: (
                  <RoutablePage title='Add trusted environment'>
                    <CreateDeploymentTrustRelationship />
                  </RoutablePage>
                ),
              },
              {
                path: 'security/trust/accounts/:trustRelationshipId',
                element: (
                  <RoutablePage title='Manage trust'>
                    <ManageAccountTrustRelationship />
                  </RoutablePage>
                ),
              },
              {
                path: 'security/trust/accounts',
                element: (
                  <RoutablePage title='Add trusted environment'>
                    <ManageAccountTrustRelationship />
                  </RoutablePage>
                ),
              },
              {
                path: 'security/trust/external/:trustRelationshipId',
                element: (
                  <RoutablePage title='Manage trust'>
                    <ManageExternalTrustRelationship />
                  </RoutablePage>
                ),
              },
              {
                path: 'security/trust/external',
                element: (
                  <RoutablePage title='Add trusted environment'>
                    <ManageExternalTrustRelationship />
                  </RoutablePage>
                ),
              },
              {
                path: 'security/trust/direct/:trustRelationshipId',
                element: (
                  <RoutablePage title='Manage trust'>
                    <ManageDirectTrustRelationship />
                  </RoutablePage>
                ),
              },
              {
                path: 'security/trust/direct',
                element: (
                  <RoutablePage title='Add trusted environment'>
                    <ManageDirectTrustRelationship />
                  </RoutablePage>
                ),
              },
              {
                path: 'operations',
                element: (
                  <RoutablePage title='Operations'>
                    <DeploymentOperations />
                  </RoutablePage>
                ),
              },
              {
                path: 'operations/heap-dumps',
                element: (
                  <RoutablePage title='Heap dumps'>
                    <HeapDumps />
                  </RoutablePage>
                ),
              },
              {
                path: 'migrate-to-index-lifecycle-management',
                element: (
                  <RoutablePage title='Migrate to Index Lifecycle Management'>
                    <IlmMigration />
                  </RoutablePage>
                ),
              },
            ],
          },
        ],
      },
    ]
  }

  function getSaasUsersRoutes(): RouteObject[] {
    const lookupSaasUsers = isFeatureActivated(state, Feature.lookupSaasUsers)

    if (!lookupSaasUsers) {
      return []
    }

    return [
      {
        path: 'users',
        children: [
          {
            index: true,
            element: (
              <RoutablePage title='Users'>
                <Users />
              </RoutablePage>
            ),
          },
          {
            path: ':userId',
            element: (
              <RoutablePage title='User Overview'>
                <User />
              </RoutablePage>
            ),
          },
        ],
      },
    ]
  }

  function getOrganizationsRoutes(): RouteObject[] {
    const lookupSaasUsers = isFeatureActivated(state, Feature.lookupSaasUsers)

    if (!lookupSaasUsers) {
      return []
    }

    return [
      {
        path: 'organizations',
        children: [
          {
            index: true,
            element: (
              <RoutablePage title='Organizations'>
                <Organizations />
              </RoutablePage>
            ),
          },
          {
            path: ':organizationId',
            children: [
              {
                index: true,
                element: (
                  <RoutablePage title='Organization Overview'>
                    <Organization selectedTabId={null} />
                  </RoutablePage>
                ),
              },
              {
                path: 'members',
                element: (
                  <RoutablePage title='Organization Members'>
                    <Organization selectedTabId='members' />
                  </RoutablePage>
                ),
              },
              {
                path: 'deployments',
                element: (
                  <RoutablePage title='Organization Deployments'>
                    <Organization selectedTabId='deployments' />
                  </RoutablePage>
                ),
              },
              {
                path: 'projects',
                element: (
                  <RoutablePage title='Organization Projects'>
                    <Organization selectedTabId='projects' />
                  </RoutablePage>
                ),
              },
              ...getConnectedClustersRoutes(),
              {
                path: 'billing-overview',
                element: (
                  <RoutablePage title='Organization Billing Overview'>
                    <Organization selectedTabId='billing-overview' />
                  </RoutablePage>
                ),
              },
              {
                path: 'usage',
                element: (
                  <RoutablePage title='Organization Usage'>
                    <Organization selectedTabId='usage' />
                  </RoutablePage>
                ),
              },
              {
                path: 'billing-history',
                element: (
                  <RoutablePage title='Organization Billing History'>
                    <Organization selectedTabId='billing-history' />
                  </RoutablePage>
                ),
              },
              {
                path: 'billing-budgets',
                element: (
                  <RoutablePage title='Organization Billing Budgets and notifications'>
                    <Organization selectedTabId='billing-budgets' />
                  </RoutablePage>
                ),
              },
              {
                path: 'billing-budgets/create',
                element: (
                  <RoutablePage title='Organization Billing Create Budget'>
                    <Organization selectedTabId='billing-budgets' />
                  </RoutablePage>
                ),
              },
              {
                path: 'billing-budgets/:budgetId/edit',
                element: (
                  <RoutablePage title='Organization Billing Edit Budget'>
                    <Organization selectedTabId='billing-budgets' />
                  </RoutablePage>
                ),
              },
              {
                path: 'security',
                element: (
                  <RoutablePage title='Organization Security'>
                    <Organization selectedTabId='security' />
                  </RoutablePage>
                ),
              },
            ],
          },
        ],
      },
    ]
  }

  function getPlatformRoutes(): RouteObject[] {
    return [
      {
        path: 'platform',
        element: (
          <RoutablePage title='Platform'>
            <PlatformRegionsRoute />
          </RoutablePage>
        ),
      },
      {
        path: 'manage',
        element: (
          <RoutablePage title='Regions'>
            <ManageRegions />
          </RoutablePage>
        ),
      },
      {
        path: 'activity-feed',
        element: (
          <RoutablePage title='Activity Feed'>
            <StackActivityFeedRoute />
          </RoutablePage>
        ),
      },
      {
        path: 'region/:regionId',
        element: (
          <RegionWrapper>
            <Region />
          </RegionWrapper>
        ),
        children: [
          ...getRegionPlatformRoutes(),
          ...getCorePlatformRoutes(),
          ...getHostsRoutes(),
          ...getAdminconsolesRoutes(),
          ...getContainerSetRoutes(),
          ...getElasticStackRoutes(),
          ...getTrustManagementRoutes(),
          ...getSnapshotRepositoryRoutes(),
          ...getDeploymentTemplatesRoutes(),
          ...getManageRbacRoutes(),
        ],
      },
    ]

    function getRegionPlatformRoutes(): RouteObject[] {
      return [
        {
          path: 'platform',
          element: (
            <RoutablePage title='Platform summary'>
              <Platform />
            </RoutablePage>
          ),
        },
      ]
    }

    function getCorePlatformRoutes(): RouteObject[] {
      return [
        {
          path: 'security',
          element: (
            <RoutablePage title='Security'>
              <RegionSecurity />
            </RoutablePage>
          ),
        },

        {
          path: 'settings',
          element: (
            <RoutablePage title='Settings'>
              <RegionSettings />
            </RoutablePage>
          ),
        },
      ]
    }

    function getHostsRoutes(): RouteObject[] {
      return [
        {
          path: 'hosts',
          children: [
            {
              index: true,
              element: (
                <RoutablePage title='Hosts'>
                  <Hosts />
                </RoutablePage>
              ),
            },
            {
              path: 'allocators',
              element: (
                <RoutablePage title='Allocators'>
                  <Hosts hostsType='allocators' />
                </RoutablePage>
              ),
            },
            {
              path: 'proxies',
              element: (
                <RoutablePage title='Proxies'>
                  <Hosts hostsType='proxies' />
                </RoutablePage>
              ),
            },
            {
              path: 'control-planes',
              element: (
                <RoutablePage title='Control planes'>
                  <Hosts hostsType='control-planes' />
                </RoutablePage>
              ),
            },
            ...getIndividualHostRoutes(),
          ],
        },
      ]

      function getIndividualHostRoutes(): RouteObject[] {
        return [
          {
            path: ':hostId',
            element: <Runner />,
            children: [
              {
                index: true,
                element: (
                  <RoutablePage title='Host overview'>
                    <Host />
                  </RoutablePage>
                ),
              },

              {
                path: 'allocator',
                element: (
                  <RoutablePage title='Allocator'>
                    <Host hostType='allocator' />
                  </RoutablePage>
                ),
              },

              {
                path: 'proxy',
                element: (
                  <RoutablePage title='Proxy'>
                    <Host hostType='proxy' />
                  </RoutablePage>
                ),
              },

              {
                path: 'control-plane',
                element: (
                  <RoutablePage title='Control plane'>
                    <Host hostType='control-plane' />
                  </RoutablePage>
                ),
              },
            ],
          },
        ]
      }
    }

    function getAdminconsolesRoutes(): RouteObject[] {
      if (!isFeatureActivated(state, Feature.adminConsoles)) {
        return []
      }

      return [
        {
          path: 'admin-consoles',
          children: [
            {
              index: true,
              element: (
                <RoutablePage title='Admin consoles'>
                  <Adminconsoles />
                </RoutablePage>
              ),
            },
          ],
        },
      ]
    }

    function getContainerSetRoutes(): RouteObject[] {
      if (!isFeatureActivated(state, Feature.containerSets)) {
        return []
      }

      return [
        {
          path: 'container-sets',
          children: [
            {
              index: true,
              element: (
                <RoutablePage title='Container Sets'>
                  <ContainerSets />
                </RoutablePage>
              ),
            },
            {
              path: ':containerSetId',
              children: [
                {
                  index: true,
                  element: (
                    <RoutablePage title='Container Set'>
                      <ContainerSet />
                    </RoutablePage>
                  ),
                },

                {
                  path: 'containers/:containerId',
                  element: (
                    <RoutablePage title='Container'>
                      <Container />
                    </RoutablePage>
                  ),
                },
              ],
            },
          ],
        },
      ]
    }

    function getElasticStackRoutes(): RouteObject[] {
      return [
        {
          path: 'elastic-stack',
          children: [
            {
              index: true,
              element: (
                <RoutablePage title='Elastic Stack versions'>
                  <ElasticStackVersions />
                </RoutablePage>
              ),
            },
            {
              path: ':versionId',
              element: (
                <RoutablePage title='Elastic Stack version details'>
                  <ElasticStackVersions />
                </RoutablePage>
              ),
            },
          ],
        },
      ]
    }

    function getTrustManagementRoutes(): RouteObject[] {
      if (!isFeatureActivated(state, Feature.crossEnvCCSCCR)) {
        return []
      }

      return [
        {
          path: 'trust-management',
          element: (
            <RoutablePage title='Trust Management'>
              <TrustManagement />
            </RoutablePage>
          ),
        },
      ]
    }

    function getSnapshotRepositoryRoutes(): RouteObject[] {
      const defaultSnapshotRepository = isFeatureActivated(state, Feature.defaultSnapshotRepository)

      if (defaultSnapshotRepository) {
        return []
      }

      return [
        {
          path: 'repositories',
          children: [
            {
              index: true,
              element: (
                <RoutablePage title='Snapshot Repositories'>
                  <SnapshotRepositoryManagement />
                </RoutablePage>
              ),
            },
            {
              path: 'create',
              element: (
                <RoutablePage title='Create Snapshot Repository'>
                  <CreateSnapshotRepository />
                </RoutablePage>
              ),
            },
            {
              path: ':repositoryId/edit',
              element: (
                <RoutablePage title='Edit Snapshot Repository'>
                  <EditSnapshotRepository />
                </RoutablePage>
              ),
            },
          ],
        },
      ]
    }

    function getDeploymentTemplatesRoutes(): RouteObject[] {
      return [
        {
          path: 'templates',
          element: <Topology />,
          children: [
            {
              path: 'deployments',
              children: [
                {
                  index: true,
                  element: (
                    <RoutablePage title='Deployment Templates'>
                      <TopologyDeploymentTemplateList />
                    </RoutablePage>
                  ),
                },
                {
                  path: 'create',
                  element: (
                    <RoutablePage title='Create Deployment Template'>
                      <TopologyDeploymentTemplateCreate />
                    </RoutablePage>
                  ),
                },
                {
                  path: ':templateId/edit',
                  element: (
                    <RoutablePage title='Edit Deployment Template'>
                      <TopologyDeploymentTemplateEdit />
                    </RoutablePage>
                  ),
                },
                {
                  path: ':templateId',
                  element: (
                    <RoutablePage title='Deployment Template'>
                      <TopologyDeploymentTemplateView />
                    </RoutablePage>
                  ),
                },
              ],
            },
            {
              path: 'instance-configurations',
              children: [
                {
                  index: true,
                  element: (
                    <RoutablePage title='Instance Configurations'>
                      <TopologyInstanceConfigurationList />
                    </RoutablePage>
                  ),
                },
                {
                  path: 'create',
                  element: (
                    <RoutablePage title='Create Instance Configuration'>
                      <TopologyInstanceConfigurationCreate />
                    </RoutablePage>
                  ),
                },
                {
                  path: ':instanceId/edit',
                  element: (
                    <RoutablePage title='Edit Instance Configuration'>
                      <TopologyInstanceConfigurationEdit />
                    </RoutablePage>
                  ),
                },
                {
                  path: ':instanceId',
                  element: (
                    <RoutablePage title='Instance Configuration'>
                      <TopologyInstanceConfigurationView />
                    </RoutablePage>
                  ),
                },
              ],
            },
          ],
        },
      ]
    }

    function getManageRbacRoutes(): RouteObject[] {
      if (!manageRbac) {
        return []
      }

      return [
        {
          path: 'manage',
          children: [
            {
              path: 'users',
              element: (
                <RoutablePage title='Native users'>
                  <SecurityClusterWrapper>
                    <ManageUsers />
                  </SecurityClusterWrapper>
                </RoutablePage>
              ),
            },
            {
              path: 'keys',
              element: (
                <RoutablePage title='API keys'>
                  <SecurityClusterWrapper>
                    <ApiKeys />
                  </SecurityClusterWrapper>
                </RoutablePage>
              ),
            },
            {
              path: 'authentication-providers',
              children: [
                {
                  index: true,
                  element: (
                    <RoutablePage title='Authentication providers'>
                      <SecurityClusterWrapper>
                        <ExternalAuthenticationList />
                      </SecurityClusterWrapper>
                    </RoutablePage>
                  ),
                },
                {
                  path: 'active-directory/create',
                  element: (
                    <RoutablePage title='Add Active Directory provider'>
                      <SecurityClusterWrapper>
                        <AddActiveDirectoryProvider />
                      </SecurityClusterWrapper>
                    </RoutablePage>
                  ),
                },
                {
                  path: 'active-directory/:realmId',
                  element: (
                    <RoutablePage title='Edit Active Directory provider'>
                      <SecurityClusterWrapper>
                        <EditActiveDirectoryProvider />
                      </SecurityClusterWrapper>
                    </RoutablePage>
                  ),
                },
                {
                  path: 'ldap/create',
                  element: (
                    <RoutablePage title='Add LDAP provider'>
                      <SecurityClusterWrapper>
                        <AddLdapProvider />
                      </SecurityClusterWrapper>
                    </RoutablePage>
                  ),
                },
                {
                  path: 'ldap/:realmId',
                  element: (
                    <RoutablePage title='Edit LDAP provider'>
                      <SecurityClusterWrapper>
                        <EditLdapProvider />
                      </SecurityClusterWrapper>
                    </RoutablePage>
                  ),
                },
                {
                  path: 'saml/create',
                  element: (
                    <RoutablePage title='Add SAML provider'>
                      <SecurityClusterWrapper>
                        <AddSamlProvider />
                      </SecurityClusterWrapper>
                    </RoutablePage>
                  ),
                },
                {
                  path: 'saml/:realmId',
                  element: (
                    <RoutablePage title='Edit SAML provider'>
                      <SecurityClusterWrapper>
                        <EditSamlProvider />
                      </SecurityClusterWrapper>
                    </RoutablePage>
                  ),
                },
              ],
            },
          ],
        },
      ]
    }
  }

  function getAccountLevelRoutes(): RouteObject[] {
    return [
      {
        // this is an RBAC thing in ECE that renders an almost blank page in ESS AC 🤷‍♂️
        path: 'settings',
        element: (
          <RoutablePage title='Settings'>
            <UserSettings />
          </RoutablePage>
        ),
      },
      {
        path: 'keys',
        element: (
          <RoutablePage title='API keys'>
            <ApiKeys />
          </RoutablePage>
        ),
      },
    ]
  }
}

function getConnectedClustersRoutes() {
  if (isCCMFeatureActivated()) {
    return [
      {
        path: 'connected-clusters',
        element: (
          <RoutablePage title='Connected clusters'>
            <Organization selectedTabId='connected-clusters' />
          </RoutablePage>
        ),
      },
    ]
  }

  return []
}
